import { faEnvelope } from '@fortawesome/pro-light-svg-icons/faEnvelope';
import { faPhone } from '@fortawesome/pro-light-svg-icons/faPhone';
import useInView from '@propertypal/shared/src/hooks/useInView';
import noPhoto from '@propertypal/shared/src/resources/icons/property/no-photo-icon.png';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import { Agent, Property } from '@propertypal/shared/src/types/property';
import Link from 'next/link';
import router from 'next/router';
import React, { FunctionComponent, useEffect, useRef } from 'react';
import { useTheme } from 'styled-components';
import Button from '../buttons/Button';
import { ButtonRow, Container, PropertyLinkImage, PropertyRow } from './FeaturedSearchAgent.style';

interface Props {
  agent?: Agent;
  onCallClick: (agent: Agent) => void;
}

const FeaturedSearchAgent: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  // @ts-ignore
  const agentUrl: string = props.agent?.landingPageUrl?.url || '';
  const containerRef = useRef<HTMLDivElement | null>(null);
  const inView = useInView(containerRef, { threshold: 0.5 });

  const trackClick = (eventName: string) => () => {
    trackGaEvent(eventName, {
      page: 'SRP',
      location: 'Featured Agent Ad',
      account_number: props.agent?.accountNumber,
    });
  };

  const trackPropertyClick = (property: Property) => {
    trackGaEvent('featured_agent_property_click', {
      page: 'SRP',
      location: 'Featured Agent Ad',
      account_number: props.agent?.accountNumber,
      property_id: property.id,
    });
  };

  useEffect(() => {
    if (inView && containerRef.current && props.agent) {
      trackGaEvent('featured_agent_impression', {
        page: 'SRP',
        location: 'Featured Agent Ad',
        account_number: props.agent.accountNumber,
      });
    }
  }, [inView]);

  if (!props.agent) {
    return null;
  }

  return (
    <Container ref={containerRef}>
      <h3>Featured Agent</h3>

      <Link href={agentUrl} prefetch={false}>
        <img
          src={props.agent.logo?.standard}
          alt={props.agent.organisation}
          style={{ maxWidth: '100%', margin: '10px auto' }}
        />
      </Link>

      <p>{props.agent.displayAddress}</p>

      {props.agent.properties?.length && (
        <PropertyRow>
          {props.agent.properties.map((property) => (
            <PropertyLinkImage
              key={property.id}
              href={property.path}
              style={{ backgroundImage: `url(${property.images?.length ? property.images[0].url : noPhoto.src})` }}
              onClick={() => trackPropertyClick(property)}
              data-testid="featured-agent-property"
            />
          ))}
        </PropertyRow>
      )}

      <p style={{ color: theme.primary }}>
        <Link href={agentUrl} prefetch={false} onClick={trackClick('featured_agent_more_click')}>
          More from {props.agent.organisation}
        </Link>
      </p>

      <ButtonRow>
        <Button
          icon={faEnvelope}
          color={theme.background}
          fontColor={theme.textDark}
          iconColour={theme.textDark}
          containerStyle={{ border: `1px solid ${theme.textDark}` }}
          onClick={() => {
            if (props.agent) {
              trackClick('featured_agent_email_click')();
              // @ts-ignore
              router.push(props.agent.landingPageUrl.url);
            }
          }}
        >
          Email
        </Button>
        <Button
          icon={faPhone}
          color={theme.background}
          fontColor={theme.textDark}
          iconColour={theme.textDark}
          containerStyle={{ border: `1px solid ${theme.textDark}` }}
          onClick={() => {
            if (props.agent) {
              trackClick('featured_agent_phone_reveal')();
              props.onCallClick(props.agent);
            }
          }}
        >
          Call
        </Button>
      </ButtonRow>
    </Container>
  );
};

export default FeaturedSearchAgent;
